import { Folder, ArrowRight } from 'lucide-react';
import { ItemIcon } from '../ItemIcon';
import { ShopChangelogEntry } from '../../types/shopChangelog';
import { cn } from '../../utils/cn';
import { formatPrice } from './priceFormat';

export type ShopChangelogEntryRowVariant = 'added' | 'removed' | 'modified-header' | 'plain';

interface ShopChangelogEntryRowProps {
  entry: ShopChangelogEntry;
  clusterId: string;
  variant?: ShopChangelogEntryRowVariant;
  /** Optional override of which slot to display (modified entries may want `mod.slot`). */
  slotOverride?: number;
  className?: string;
}

const VARIANT_CLASSES: Record<ShopChangelogEntryRowVariant, string> = {
  added:             'bg-green-500/10 border-l-2 border-green-400/60',
  removed:           'bg-red-500/10 border-l-2 border-red-400/60',
  'modified-header': 'bg-yellow-500/10 border-l-2 border-yellow-400/60',
  plain:             'border-l-2 border-glass-200/20',
};

/**
 * One shop entry rendered as a single row inside the diff body. Used by
 * `ShopChangelogDetail` for added/removed/modified blocks.
 */
export function ShopChangelogEntryRow({
  entry,
  clusterId,
  variant = 'plain',
  slotOverride,
  className,
}: ShopChangelogEntryRowProps) {
  const slot = slotOverride ?? entry.slot;
  const isPage = entry.type === 'PAGE';
  const itemId = entry.stack?.id ?? '';

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-2 py-1.5 rounded-r-md text-sm',
        VARIANT_CLASSES[variant],
        className,
      )}
    >
      <span className="font-mono text-[10px] text-glass-400 bg-dark-900/40 px-1.5 py-0.5 rounded shrink-0 min-w-[44px] text-center">
        Slot {slot}
      </span>

      <div className="relative flex items-center shrink-0">
        {itemId ? (
          <ItemIcon displayMode="inline" clusterId={clusterId} itemId={itemId} className="w-5 h-5 object-contain" />
        ) : (
          <span className="text-glass-400 italic">No item</span>
        )}
        {isPage && (
          <span
            title="Page navigation entry"
            className="absolute -bottom-0.5 -right-1 bg-blue-500/80 rounded-sm p-[1px]"
          >
            <Folder className="w-2.5 h-2.5 text-white" />
          </span>
        )}
      </div>

      {entry.stack?.count != null && entry.stack.count > 1 && (
        <span className="font-mono text-xs text-glass-300 shrink-0">×{entry.stack.count}</span>
      )}

      <div className="flex-1 min-w-0" />

      <div className="flex items-center gap-3 text-xs shrink-0">
        {isPage ? (
          <span className="inline-flex items-center gap-1 text-blue-300 font-mono">
            <ArrowRight className="w-3 h-3" />
            {entry.pageId ?? '—'}
          </span>
        ) : (
          <ListingDataInline entry={entry} />
        )}
      </div>
    </div>
  );
}

function ListingDataInline({ entry }: { entry: ShopChangelogEntry }) {
  const buy = formatPrice(entry.buyPrice);
  const sell = formatPrice(entry.sellPrice);
  const batch = entry.count;

  return (
    <>
      <span className="inline-flex items-center gap-1">
        <span className="text-glass-400">Buy</span>
        <span
          className={cn(
            'font-semibold',
            buy.tone === 'normal' && 'text-green-300',
            buy.tone === 'disabled' && 'text-glass-500 italic',
            buy.tone === 'unknown' && 'text-glass-400',
          )}
        >
          {buy.label}
        </span>
      </span>
      <span className="text-glass-600">·</span>
      <span className="inline-flex items-center gap-1">
        <span className="text-glass-400">Sell</span>
        <span
          className={cn(
            'font-semibold',
            sell.tone === 'normal' && 'text-blue-300',
            sell.tone === 'disabled' && 'text-glass-500 italic',
            sell.tone === 'unknown' && 'text-glass-400',
          )}
        >
          {sell.label}
        </span>
      </span>
      {batch != null && (
        <>
          <span className="text-glass-600">·</span>
          <span className="text-glass-300 font-mono">×{batch}</span>
        </>
      )}
    </>
  );
}
